import { z } from 'zod';
import { Id, IsoDateTime, Currency } from './common.js';

export const BusinessType = z.enum([
  'ecommerce_platform',
  'ecommerce_direct',
  'service_business',
  'saas_support',
  'hybrid',
]);
export type BusinessType = z.infer<typeof BusinessType>;

export const FeatureFlag = z.enum([
  'products',
  'appointments',
  'knowledge_base',
  'crm',
  'support_tickets',
  'platform_integration',
]);
export type FeatureFlag = z.infer<typeof FeatureFlag>;

export const LanguageMode = z.enum(['auto_detect', 'strict_primary']);
export const UpsellLevel = z.enum(['off', 'low', 'medium', 'high']);
export const ResponseSpeed = z.enum(['instant', 'natural', 'slow']);
export const WorkingHoursMode = z.enum(['always', 'business_hours_only']);

export const Merchant = z.object({
  merchant_id: Id,
  clerk_org_id: z.string(),
  name: z.string(),
  business_type: BusinessType,
  features: z.array(FeatureFlag),
  primary_language_id: z.string(),
  supported_languages: z.array(z.string()),
  language_mode: LanguageMode,
  brand_voice: z.string().max(4096).default(''),
  upsell_level: UpsellLevel.default('off'),
  response_speed: ResponseSpeed.default('natural'),
  working_hours_mode: WorkingHoursMode.default('always'),
  escalation_threshold: z.number().int().min(0).default(2),
  platform_id: Id.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Merchant = z.infer<typeof Merchant>;

export const BusinessProfile = z.object({
  merchant_id: Id,
  description: z.string().max(2000).default(''),
  category: z.string().max(120).default(''),
  founding_date: z.string().nullable().optional(),
  slogan: z.string().max(280).default(''),
  logo_url: z.string().nullable().optional(),
  cover_image_url: z.string().nullable().optional(),
  languages_spoken: z.array(z.string()).default([]),
  supported_regions: z.array(z.string()).default([]),
  website_url: z.string().nullable().optional(),
  social_handles: z.record(z.string()).default({}),
  payment_methods: z.array(z.string()).default([]),
});
export type BusinessProfile = z.infer<typeof BusinessProfile>;

export const BusinessLocation = z.object({
  location_id: Id,
  merchant_id: Id,
  name: z.string(),
  address: z.string(),
  phone: z.string().nullable().optional(),
  manager: z.string().nullable().optional(),
  services_available: z.array(Id).default([]),
});

export const DayOfWeek = z.number().int().min(0).max(6);
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const BusinessHours = z.object({
  merchant_id: Id,
  day_of_week: DayOfWeek,
  open_time: TimeOfDay.nullable(),
  close_time: TimeOfDay.nullable(),
  is_closed: z.boolean(),
  notes: z.string().nullable().optional(),
});

export const BusinessHoliday = z.object({
  holiday_id: Id,
  merchant_id: Id,
  date: z.string(),
  is_closed: z.boolean(),
  open_time: TimeOfDay.nullable().optional(),
  close_time: TimeOfDay.nullable().optional(),
  notes: z.string().nullable().optional(),
});

export const BusinessPolicies = z.object({
  merchant_id: Id,
  cancellation: z.string().max(4096).default(''),
  refund: z.string().max(4096).default(''),
  return: z.string().max(4096).default(''),
  privacy: z.string().max(4096).default(''),
  payment_terms: z.string().max(4096).default(''),
  delivery_zones: z
    .array(
      z.object({
        name: z.string(),
        fee_cents: z.number().int().nonnegative(),
        currency: Currency,
        eta_hours: z.number().int().nullable().optional(),
      }),
    )
    .default([]),
});
